<template>
  <HelpTip v-if="importType && importType.tooltip">
    {{ importType.tooltip }}
  </HelpTip>
  <HelpTip v-if="importType && importType.type === 'coco'">
    Select a folder containing a COCO dataset to upload. For more information on COCO Dataset format, <a href="https://cocodataset.org/#overview" target="_blank" rel="noopener noreferrer">view the documentation</a>
  </HelpTip>
  <HelpTip v-if="importType && importType.type === 'edgefirst'">
    EdgeFirst Studio proprietary dataset format. If uploading .zip and .arrow, at least one .zip file must be selected. If uploading .mcap files, no .zip or .arrow file is required.
    For more information on EdgeFirst Dataset format, <a href="https://doc.edgefirst.ai/latest/datasets/format/" target="_blank" rel="noopener noreferrer">view the documentation</a>.
  </HelpTip>
  <div class="row mt-3">
    <div class="col-9 settings__control-group">
      <label>Import Type</label>
      <select
        id="type-select"
        v-model="importType"
        class="select"
        required
      >
        <option v-for="(option, i) in importTypes" :key="`import-type-${i}`" :value="option">
          {{ option.label }}
        </option>
      </select>
    </div>
  </div>
  <div v-if="importType.type === 'edgefirst'" class="col">
    <div class="file-upload-container">
      <input
        id="edgefirst-select"
        class="d-none"
        type="file"
        multiple
        accept=".zip,.arrow,.mcap"
        @change="handleSelectFileInput"
      >
      <div
        :disabled="parsingSnasphotFiles"
        class="file-upload-btn mt-3"
        :class="{_highlight: dropHighlighted}"
        @dragenter.prevent="handleDragEnter"
        @dragover.prevent=""
        @dragleave="handleDragLeave"
        @drop="dropHighlighted = false"
        @drop.stop.prevent="($event) => handleFileDrop($event, ['zip', 'mcap', 'arrow'])"
      >
        <SVGIcon
          id="upload-icon"
          class="file-upload-btn__upload-icon"
          :iconName="'cloud_upload'"
          :width="'100%'"
          :height="'200px'"
        />
        <div id="file-upload-btn-text" class="file-upload-btn__text">
          <span>Drag and drop or <span class="highlight" @click="chooseFiles('edgefirst-select')">select files</span></span>
        </div>
        <div v-if="fileObjs && fileObjs.length > 0" id="file-upload-grid" class="file-grid scrollbar">
          <div v-for="(file, i) in fileObjs" :key="`file-${i}`" class="file-grid__cell">
            <span>{{ file.name }}</span>
            <IconButton
              :id="`file-${i}-remove-icon`"
              class="file-grid__remove-icon"
              :icon="'cancel_filled'"
              :width="16"
              :height="16"
              @click="removeFile(i)"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="row mt-1">
      <v-checkbox
        v-model="mcapTopics"
        color="#250e81"
        :hide-details="true"
        :density="'compact'"
        class="non-opaque-label"
      >
        <template #label>
          <span class="checkbox-label">Use MCAP Selected Topics</span>
          <SVGIcon
            v-tooltip="`Only selected topics will be used for mcap file publishing and will be shown on the result dataset.
                     An mcap file is required in the snapshot for this functionality to take effect. Else, all topics are stored.
                     Camera topics (JPEG and H264) are always enabled to ensure meaningful data.`"
            :width="'20px'"
            :height="'20px'"
            :iconName="'info'"
            class="help-icon"
          />
        </template>
      </v-checkbox>
    </div>
    <div v-if="mcapTopics" class="row ml-3 mt-1">
      <div class="col-12 topics-grid">
        <v-checkbox
          v-for="(topic,i) in topics"
          :key="i"
          v-model="selectedTopics"
          color="#250e81"
          :hide-details="true"
          :density="'compact'"
          :value="topic.value"
          class="non-opaque-label"
          :disabled="topic.value === '/camera/jpeg' || topic.value === '/camera/h264'"
        >
          <template #label>
            <span class="checkbox-label">{{ topic.display }}</span>
          </template>
        </v-checkbox>
      </div>
    </div>
    <div class="row mt-3">
      <v-checkbox
        v-model="useSnapshotFPS"
        color="#250e81"
        :hide-details="true"
        :density="'compact'"
        class="non-opaque-label"
      >
        <template #label>
          <span class="checkbox-label">Use MCAP Frame Rate</span>
          <SVGIcon
            v-tooltip="`Adjust the frame rate (FPS) to process mcap files in the snapshot at. This will affect Depth Generation and AI Ground Truth Generation.`"
            :width="'20px'"
            :height="'20px'"
            :iconName="'info'"
            class="help-icon"
          />
        </template>
      </v-checkbox>
    </div>
    <div v-if="useSnapshotFPS" class="row ml-3 mt-1">
      <div class="col-6 settings__control-group">
        <label>FPS</label>
        <input
          v-model="snapshotFPS"
          class="input"
          type="number"
          min="1"
        >
      </div>
    </div>
    <div class="row mt-3">
      <v-checkbox
        v-model="depthGen"
        color="#250e81"
        :hide-details="true"
        :density="'compact'"
        class="non-opaque-label"
      >
        <template #label>
          <span class="checkbox-label">Depth Generation</span>
          <SVGIcon
            v-tooltip="`Generate Depth Maps for frames in snapshot.`"
            :width="'20px'"
            :height="'20px'"
            :iconName="'info'"
            class="help-icon"
          />
        </template>
      </v-checkbox>
    </div>
    <div class="row mt-3">
      <v-checkbox
        v-model="agtgPipeline"
        color="#250e81"
        :hide-details="true"
        :density="'compact'"
        class="non-opaque-label"
      >
        <template #label>
          <span class="checkbox-label">AI Ground Truth Generation</span>
          <SVGIcon
            v-tooltip="`Generate 2D Bounding Boxes and Segmentation Masks for frames in snapshot. 3D Bounding Boxes are also generated if LiDAR or Radar files are available.`"
            :width="'20px'"
            :height="'20px'"
            :iconName="'info'"
            class="help-icon"
          />
        </template>
      </v-checkbox>
    </div>
  </div>
  <template v-for="(arg, i) in importType.args" :key="`importType-arg-${i}`">
    <div v-if="arg.type === 'text'" class="row">
      <div class="settings__control-group">
        <label :class="{'required' : !arg.optional}">{{ arg.label }}</label>
        <div class="source-row">
          <input
            v-model="textInput[arg.label]"
            class="source-row__text-input input"
            type="text"
          >
        </div>
      </div>
    </div>
    <div v-if="arg.type === 'folder'" class="col">
      <div class="file-upload-container">
        <div
          class="file-upload-btn mt-3"
          :class="{_highlight: dropHighlighted}"
          @dragenter.prevent="handleDragEnter"
          @dragover.prevent=""
          @dragleave="handleDragLeave"
          @drop="dropHighlighted = false"
          @drop.stop.prevent="($event) => handleFileDrop($event)"
        >
          <SVGIcon
            v-if="!parsingFiles"
            id="upload-icon"
            class="file-upload-btn__upload-icon"
            :iconName="'cloud_upload'"
            :width="'100%'"
            :height="'200px'"
          />
          <div v-else class="file-upload-btn__parsing-files">
            <div id="loading-visualization"><div class="lds-ring"><div /><div /><div /><div /></div></div>
          </div>
          <div id="file-upload-btn-text" class="file-upload-btn__text">
            <span>Drag and drop files or <span class="highlight" @click="!parsingFiles ? chooseSourceFolder() : null">select parent folder</span></span>
          </div>
          <template v-if="fileObjs && fileObjs.length <= 20">
            <div v-if="fileObjs && fileObjs.length > 0" id="file-upload-grid" class="file-grid scrollbar">
              <div v-for="(file, i) in fileObjs" :key="`file-${i}`" class="file-grid__cell">
                <span>{{ file.name }}</span>
                <IconButton
                  :id="`file-${i}-remove-icon`"
                  class="file-grid__remove-icon"
                  :icon="'cancel_filled'"
                  :width="16"
                  :height="16"
                  @click="removeFile(i)"
                />
              </div>
            </div>
          </template>
          <div v-else-if="fileObjs && fileObjs.length > 20" class="file-upload-btn__text">
            <span>{{ fileObjs.length }} files selected</span>
          </div>
        </div>
      </div>
    </div>
    <div v-if="arg.type === 'file'" class="row">
      <!-- <div class="settings__control-group">
        <label :class="{'required' : !arg.optional}">{{ arg.label }}</label>
        <div class="source-row">
          <input
            :value="files && files.length > 0 ? `${files.length} file(s) selected` : ''"
            class="source-row__folder-input input"
            type="text"
            :placeholder="importType.type === 'video_frames' ? 'Select a video file' : 'Select files'"
            readonly
          >
          <button class="source-row__folder-btn button-secondary" :class="{'button-spinner': parsingFiles}" @click="chooseSourceFile">SELECT FILE</button>
          <input
            v-show="false"
            id="import-script-file-input"
            ref="importFile"
            type="file"
            :multiple="importType.type !== 'video_frames'"
            @change="sourceFileSelected"
          >
        </div>
      </div> -->
    </div>
    <div v-if="arg.type === 'mixed'" class="col">
      <div class="file-upload-container">
        <input
          id="file-select-mixed"
          class="d-none"
          type="file"
          multiple
          @change="handleSelectFileInput"
        >
        <input
          id="file-select-mixed-folder"
          class="d-none"
          type="file"
          multiple
          webkitdirectory
          mozdirectory
          msdirectory
          odirectory
          directory
          @change="handleSelectFileInput"
        >
        <div
          class="file-upload-btn mt-3"
          :class="{_highlight: dropHighlighted}"
          @dragenter.prevent="handleDragEnter"
          @dragover.prevent=""
          @dragleave="handleDragLeave"
          @drop="dropHighlighted = false"
          @drop.stop.prevent="($event) => handleFileDrop($event)"
        >
          <SVGIcon
            v-if="!parsingFiles"
            id="upload-icon"
            class="file-upload-btn__upload-icon"
            :iconName="'cloud_upload'"
            :width="'100%'"
            :height="'200px'"
          />
          <div v-else class="file-upload-btn__parsing-files">
            <div id="loading-visualization"><div class="lds-ring"><div /><div /><div /><div /></div></div>
          </div>
          <div id="file-upload-btn-text" class="file-upload-btn__text">
            <span>Drag and drop, <span class="highlight" @click="!parsingFiles ? chooseFiles('file-select-mixed') : null">select files</span>, or
              <span class="highlight" @click="!parsingFiles ? chooseSourceFolder() : null">select parent folder</span>
            </span>
          </div>
          <template v-if="fileObjs && fileObjs.length <= 20">
            <div v-if="fileObjs && fileObjs.length > 0" id="file-upload-grid" class="file-grid scrollbar">
              <div v-for="(file, i) in fileObjs" :key="`file-${i}`" class="file-grid__cell">
                <span>{{ file.name }}</span>
                <IconButton
                  :id="`file-${i}-remove-icon`"
                  class="file-grid__remove-icon"
                  :icon="'cancel_filled'"
                  :width="16"
                  :height="16"
                  @click="removeFile(i)"
                />
              </div>
            </div>
          </template>
          <div v-else-if="fileObjs && fileObjs.length > 20" class="file-upload-btn__text">
            <span>{{ fileObjs.length }} files selected</span>
          </div>
        </div>
      </div>
    </div>
    <div v-if="arg.type === 'folder_groups'" class="col">
      <v-checkbox
        v-model="shouldCreateImageGroups"
        density="compact"
        hide-details="true"
        label="Create image groups from folder names"
      />
    </div>
    <div v-if="arg.type === 'fps'" class="row mt-3">
      <div class="col-3 settings__control-group">
        <div class="input-label d-flex">
          <label :class="{'required' : !arg.optional}">FPS</label>
          <SVGIcon
            v-tooltip="'Frames per second (FPS) control how often the video is sampled to create result frames. A lower FPS will yield less frequent frames.'"
            :width="'20px'"
            :height="'20px'"
            :iconName="'info'"
            class="tooltip-icon"
          />
        </div>
        <div class="source-row">
          <input
            v-model="videoFPS"
            class="input"
            type="number"
          >
        </div>
      </div>
    </div>
    <div v-if="arg.type === 'dest_annotation_set'" class="row mt-3">
      <div class="col-6 settings__control-group align-items-start">
        <label :class="{'required' : !arg.optional}">Annotation Set</label>
        <select
          v-if="annotationSets && annotationSets.length > 0"
          v-model="selectedAnnotationSet"
          class="select"
        >
          <option :value="null">New annotation set</option>
          <option v-for="option in annotationSets" :key="`dest-anset-select-${option.id}`" :value="option">
            {{ option.name }}
          </option>
        </select>
      </div>
    </div>
  </template>
  <span id="file-upload-btn-subtext" class="mt-3">Upload will terminate if you exit the website or close your browser.</span>

  <div class="row mt-3">
    <div class="col-4" />
    <div class="col-8 action-buttons">
      <button
        class="button button-sm button-secondary"
        @click="handleCancel"
      >
        Cancel
      </button>
      <button
        class="button button-sm"
        :class="{'button-spinner': startingImport}"
        :disabled="startingImport || parsingFiles || (fileObjs && fileObjs.length === 0)"
        @click="handleStartImport"
      >
        Start Import
      </button>
    </div>
  </div>
</template>

<script>
import { showDirectoryPicker, showOpenFilePicker } from 'file-system-access';
import useSnapshot from '@/composables/useSnapshot';
import SVGIcon from '@/components/SVGIcon.vue';
import IconButton from '@/components/IconButton.vue';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';
import HelpTip from '@/components/HelpTip.vue';
import useTaskModal from '@/composables/useTaskModal.js';
import { useImporterStore } from '@/stores/useImporterStore.js';
import DatastoreConnect from '../../../assets/js/DatastoreFunctions/datastore-interface';

const $toast = useToast();

export default {
  name: 'DefaultImportScriptForm',
  components: {
    SVGIcon,
    IconButton,
    HelpTip,
  },
  props: {
    modelValue: {
      type: Object,
      default: () => {},
    },
    datasetID: {
      type: Number,
      default: null,
    },
    annotationSets: {
      type: Array,
      default: () => [],
    },
    isFilesParsed: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'cancel', 'upload-started', 'upload-complete', 'upload-error', 'update:modelValue', 'update:isFilesParsed', 'confirm-duplicate-upload',
  ],
  setup() {
    const {
      resumeGetTasks,
      isGettingTasks,
      getTasks,
    } = useTaskModal();

    const {
      snapshotFiles,
      processFolder,
      handleFileInput,
      compileFiles,
      processFolderAndReturnFiles,
    } = useSnapshot();

    const {
      importByType,
    } = useImporterStore();

    return {
      snapshotFiles,
      processFolder,
      handleFileInput,
      compileFiles,
      processFolderAndReturnFiles,
      resumeGetTasks,
      isGettingTasks,
      getTasks,
      importByType,
    };
  },
  data() {
    return {
      sourceDir: "",
      sourceFile: "",
      textInput: {},
      videoFPS: 10,
      selectedAnnotationSet: null,
      files: null,
      startingImport: false,
      files_parsed: 0,
      parsingFiles: false,
      shouldCancelFileParsing: false,
      groups: null,
      selectedGroup: '',
      shouldCreateImageGroups: false,
      imageGroupsToCreate: [],
      dropHighlighted: false,
      dragCounter: 0,
      fileObjs: [],
      importTypes: null,
      importType: "",
      depthGen: false,
      agtgPipeline: false,
      mcapTopics: false,
      selectedTopics: ['/camera/jpeg', '/camera/h264', '/model/boxes2d', '/model/mask', '/radar/targets', '/radar/cube', '/imu', '/gps', '/lidar/points', '/lidar/depth', '/lidar/reflect'],
      topics: [
        { display: 'Camera JPEG', value: '/camera/jpeg' },
        { display: 'Camera H264', value: '/camera/h264' },
        { display: '2D Bounding Box', value: '/model/boxes2d' },
        { display: 'Segmentation Mask', value: '/model/mask' },
        { display: 'Radar PCD', value: '/radar/targets' },
        { display: 'Radar Cube', value: '/radar/cube' },
        { display: 'Pose', value: '/imu' },
        { display: 'Location', value: '/gps' },
        { display: 'LiDAR PCD', value: '/lidar/points' },
        { display: 'LiDAR Depth Map', value: '/lidar/depth' },
        { display: 'LiDAR Reflect Map', value: '/lidar/reflect' },
      ],
      useSnapshotFPS: false,
      snapshotFPS: 1,
      videoExtensions: ["mp4", "mov", "avi", "mkv", "webm", "flv", "wmv", "mpg", "mpeg", "m4v", "3gp", "ts", "mts", "m2ts"],
    };
  },
  computed: {
    currentProject() {
      return this.$store.state.projects.currentProject;
    },
    datasetName() {
      const dataset = this.$store.state?.datasets?.datasetList.find((d) => d.id === this.datasetID);
      return dataset.name;
    },
    formValue() {
      const params = {};
      if (this.selectedAnnotationSet) {
        params.annotation_set_id = this.selectedAnnotationSet.id;
      }
      if (this.videoFPS) {
        params.fps = this.videoFPS;
      }
      return params;
    },
    importParams() {
      const importParams = {
        import_type: this.importType.type,
        dataset_id: this.datasetID,
        dataset_name: this.datasetName,
        project_id: this.currentProject.id,
        group_id: this.selectedGroup.id,
        ...this.formValue,
      };
      if (this.shouldCreateImageGroups) {
        importParams.create_image_groups = this.imageGroupsToCreate;
      }
      if (this.selectedGroup) {
        importParams.group_id = this.selectedGroup.id;
      }
      return importParams;
    },
  },
  watch: {
    formValue() {
      this.$emit('update:modelValue', this.formValue);
    },
    files_parsed() {
      if (this.files_parsed > 0) {
        this.$emit('update:isFilesParsed', true);
      } else {
        this.$emit('update:isFilesParsed', false);
      }
    },
    importType() {
      this.clearInput();
    },
  },
  async mounted() {
    this.dataConnect = new DatastoreConnect();
    await this.getSupportedTypes();
    // this.groups = await this.getDatasetGroups();
    this.$nextTick(() => {
      if (this.annotationSets && this.annotationSets.length > 0) {
        this.selectedAnnotationSet = this.annotationSets[0];
      }
    });
  },
  unmounted() {
    this.shouldCancelFileParsing = true;
  },
  methods: {
    async getSupportedTypes() {
      this.dataConnect.getSupportedImportTypes()
        .then((data) => {
          if (data.result.length > 0) {
            this.importTypes = data.result;
            this.importType = data.result[0];
          }
        })
        .catch((e) => console.log(e));
    },
    clearInput() {
      this.name = "";
      this.source = null;
      this.sourceDir = "";
      this.sourceFile = "";
      this.videoFPS = 1;
      this.message = "";
      this.textInput = {};
      this.files = [];
      this.fileObjs = [];
    },
    handleCancel() {
      this.$emit('cancel');
    },
    async getDatasetGroups() {
      return this.dataConnect.getGroupListForDataset({
        dataset_id: this.datasetID,
      })
        .then((resp) => {
          if (resp.error !== undefined) {
            throw Error(resp.error);
          }
          return resp.result;
        })
        .catch((error) => {
          throw error;
        });
    },
    validateForm() {
      if (this.importType.args.some((item) => item.type === "folder")) {
        // if (!this.sourceDir) {
        //   return "Please select a folder";
        // }

        if (this.fileObjs === null || !this.fileObjs.length) {
          return "No files found in folder";
        }
      }

      if (this.importType.args.some((item) => item.type === "file")) {
        if (!this.sourceFile) {
          return "No files found";
        }

        if (this.fileObjs === null || !this.fileObjs.length) {
          return "No files found";
        }
      }

      if (Object.values(this.importType.args).includes('fps')) {
        if (this.videoFPS === 0) {
          return "Please enter a non-zero frame number";
        }
      }

      if (Object.values(this.importType.args).includes('text')) {
        if (Object.values(this.textInput).includes('')) {
          return "Text Input can not be blank.";
        }
      }

      return null;
    },
    async handleStartImport() {
      const response = await this.dataConnect.checkQuota();
      if (response.error) {
        $toast.open({
          queue: true,
          duration: 0,
          message: response.error.message,
          type: 'error',
          dismissible: true,
          position: 'top',
        });
        this.startingImport = false;
        this.$emit('upload-complete');
        return;
      }
      const filesAvailable = response.result.quota.files - response.result.used.files;
      if (!(filesAvailable > 0)) {
        $toast.open({
          queue: true,
          duration: 0,
          message: "Files Upload Limit Exceeded",
          type: 'error',
          dismissible: true,
          position: 'top',
        });
        this.startingImport = false;
        this.$emit('upload-complete');
        return;
      }

      if (this.importType.type === 'video_frames') {
        this.handleVideoImport();
        this.getTasks();
        if (!this.isGettingTasks) {
          this.resumeGetTasks();
        }
      } else {
        this.handleStartImportScript();
      }
    },
    async handleVideoImport() {
      const fileObjsExtensionChecked = [];
      this.fileObjs.forEach((file) => {
        const parts = file.name.split('.');
        let ext = "";
        if (parts.length > 1) {
          ext = parts.pop();
        }
        if (this.videoExtensions.includes(ext)) {
          fileObjsExtensionChecked.push(file);
        }
      });

      this.startingImport = true;
      const formData = new FormData();
      for (let i = 0; i < fileObjsExtensionChecked.length; i++) {
        formData.append('video', fileObjsExtensionChecked[i]);
      }

      const paramObj = {
        fps: this.videoFPS,
        dataset_id: this.datasetID,
      };

      formData.append('params', new Blob([JSON.stringify(paramObj)], { type: 'application/json' }));

      const resp = await this.dataConnect.uploadVideo(formData)
        .catch((error) => {
          $toast.open({
            queue: true,
            duration: 0,
            message: error,
            type: 'error',
            dismissible: true,
            position: 'top',
          });
          this.startingImport = false;
        });

      if (!resp || resp.error) {
        $toast.open({
          queue: true,
          duration: 0,
          message: `Failed to upload video: ${resp.error.message}`,
          type: 'error',
          dismissible: true,
          position: 'top',
        });
      }

      this.startingImport = false;
      this.$emit('upload-complete');
    },
    async handleStartImportScript() {
      const errorMessage = this.validateForm();
      if (errorMessage) {
        $toast.open({
          queue: true,
          duration: 0,
          message: errorMessage,
          type: 'error',
          dismissible: true,
          position: 'top',
        });
        return;
      }

      this.startingImport = true;
      let resp = null;
      if (this.importType.type === 'annotations') {
        if (!this.selectedAnnotationSet) {
          this.$emit("upload-error", {
            confirmMessage: `No annotation set selected.`,
            confirmMessageHeader: 'Upload Error',
          });
          return;
        }
        resp = await this.s3MultipleAnnotationFileUpload({ urls: this.fileObjs, params: this.importParams })
          .catch((error) => {
            console.error(error);
            $toast.open({
              queue: true,
              duration: 0,
              message: error,
              type: 'error',
              dismissible: true,
              position: 'top',
            });
          });
        this.startingImport = false;
      } else if (this.importType.type === 'edgefirst') {
        const params = {
          project_id: this.currentProject.id,
          dataset_id: this.datasetID,
          dataset_name: this.datasetName,
          annotation_set_id: this.selectedAnnotationSet?.id,
          depth_gen: this.depthGen,
          agtg_pipeline: this.agtgPipeline,
          fps: this.snapshotFPS,
          enabled_topics: this.mcapTopics ? this.selectedTopic : [],
          label_names: ['person'],
        };
        this.compileFiles(params, this.fileObjs);
        resp = true;
        this.startingImport = false;
        this.$emit('upload-complete');
      } else {
        resp = await this.s3MultipleFileUpload({ fileList: this.fileObjs, params: this.importParams })
          .catch((error) => {
            console.error(error);
            $toast.open({
              queue: true,
              duration: 0,
              message: error.message,
              type: 'error',
              dismissible: true,
              position: 'top',
            });
          });
        if (resp && resp.duplicate_count > 0) {
          this.$emit('confirm-duplicate-upload', {
            duplicate_count: resp.duplicate_count,
            fullParams: { fileList: this.fileObjs, params: this.importParams },
          });
          this.startingImport = false;
          return;
        }
        this.startingImport = false;
      }
    },
    async chooseSourceFolder() {
      try {
        const directoryHandle = await showDirectoryPicker({ _preferPolyfill: false });
        this.parsingFiles = true;
        this.sourceDir = directoryHandle.name;
        this.files_parsed = 0;
        this.fileObjs = [];
        this.imageGroupsToCreate = [];
        const tree = { [directoryHandle.name]: {} };

        // Parse everything in parallel
        await this.handleDirectory(directoryHandle, tree, directoryHandle.name);

        // Wait for all file reads to finish
        this.fileObjs = await Promise.all(this.fileObjs);

        this.parsingFiles = false;
      } catch (error) {
        if (!error.toString().includes("AbortError")) {
          $toast.open({
            queue: true,
            duration: 0,
            message: error,
            type: 'error',
            dismissible: true,
            position: 'top',
          });
        }
      }
    },
    async handleDirectory(dirhandle, tree, relativePath) {
      const subtree = {};
      tree[dirhandle.name] = subtree;

      const promises = []; // store promises for subdirectory processing

      for await (const [name, handle] of dirhandle) {
        if (this.shouldCancelFileParsing) { break; }
        if (handle.kind === "directory") {
          // Get first directory level group names
          if (dirhandle.name === this.sourceDir) {
            this.imageGroupsToCreate.push(name);
          }
          const newRelativePath = relativePath ? `${relativePath}/${name}` : name;

          // Push the directory processing task to run in parallel
          promises.push(this.handleDirectory(handle, subtree, newRelativePath));
        } else {
          this.files_parsed += 1;
          subtree[name] = "[file]";
          this.fileObjs.push(
            handle.getFile().then((file) => {
              file.relativePath = `${relativePath}/${name}`;
              return file;
            }),
          );
        }
      }

      // Wait for all subdirectory processing in parallel
      await Promise.all(promises);
    },
    chooseSourceFile() {
      this.$refs['importFile'][0].click();
    },
    // sourceSelected() {
    //   this.fileObjs = this.$refs['importFolder'][0].files;
    //   if (this.fileObjs.length > 0) {
    //     const relativePath = this.fileObjs[0].webkitRelativePath;
    //     const folder = relativePath.split("/");
    //     // this.sourceDir = folder[0];
    //   }
    // },
    // sourceFileSelected() {
    //   this.fileObjs = this.$refs['importFile'][0].files;
    //   if (this.fileObjs.length > 0) {
    //     this.sourceFile = this.fileObjs[0].name;
    //   }
    // },
    async s3MultipleFileUpload({ fileList, params, ignoreDuplicate }) {
      const keys = [];
      // Validate file count and sizes, also trim paths
      for (const file of fileList) {
        let trimmedPath;
        const relativePath = file.relativePath || file.webkitRelativePath;
        if (relativePath) {
          // From folder select
          trimmedPath = relativePath.split('/').slice(1);
        } else {
          // From file select
          trimmedPath = [file.name];
        }

        keys.push(trimmedPath.join('/'));
      }

      // Check image upload limit
      // TODO: check credit using the current org credit

      // Get s3 presigned urls

      const presignedResponse = await this.s3CreatePresignedUrl({
        dataset_id: params.dataset_id,
        ignore_duplicate: ignoreDuplicate,
        keys,
      })
        .catch((error) => {
          throw error;
        });

      if (presignedResponse && presignedResponse.duplicate_count > 0 && !ignoreDuplicate) {
        return presignedResponse;
      }

      const newFileList = [];

      if (presignedResponse.presignedUrls && presignedResponse.presignedUrls.length === 0) {
        // quietly return, do nothing
        this.$emit('upload-complete');
        return null;
      }
      if (presignedResponse) {
        const {
          importerSource, indexes, presignedUrls, tnPresignedUrls,
        } = presignedResponse;

        if (importerSource) {
          params.importer_source = importerSource;
        }

        presignedUrls.forEach((url, i) => {
          newFileList.push({
            url,
            thumbnail_url: tnPresignedUrls[i],
            file: fileList[indexes[i]],
          });
        });
      }
      const importResponse = await this.importByType({
        urls: newFileList,
        importParams: params,
      });

      this.startingImport = false;
      this.$emit('upload-complete');

      return importResponse;
    },
    async handleUploadConfirmed(params) {
      return this.s3MultipleFileUpload(params);
    },
    async s3MultipleAnnotationFileUpload({ urls, params }) {
      // TODO: Check annotation upload limit

      this.startingImport = false;
      this.$emit('upload-started');

      const importResponse = await this.importByType({
        urls,
        importParams: params,
      });

      return importResponse;
    },
    async getMaxImageSize() {
      return this.dataConnect.getAccountingMaxImageSize()
        .then((resp) => {
          if (resp.error !== undefined) {
            throw Error(resp.error);
          }
          return resp.result;
        })
        .catch((error) => {
          throw error;
        });
    },
    async checkImageUploadLimit(imgCount) {
      return this.dataConnect.checkImageLimitForAccounting({
        image_count: imgCount,
      })
        .then((resp) => {
          if (resp.error !== undefined) {
            throw Error(resp.error);
          }
          return resp.result;
        })
        .catch((error) => {
          throw error;
        });
    },
    async s3CreatePresignedUrl({ dataset_id, keys, ignore_duplicate }) {
      return this.dataConnect.s3CreatePresignedUrl({
        dataset_id,
        ignore_duplicate,
        keys,
      })
        .then((resp) => {
          if (resp.error !== undefined) {
            throw Error(resp.error);
          }
          return resp.result;
        })
        .catch((error) => {
          throw error;
        });
    },
    handleDragEnter() {
      this.dragCounter += 1;
      this.dropHighlighted = true;
    },
    handleDragLeave() {
      this.dragCounter -= 1;
      if (this.dragCounter === 0) {
        this.dropHighlighted = false;
      }
    },
    async handleFileDrop(e, extensions) {
      if (this.parsingSnasphotFiles) {
        $toast.open({
          queue: true,
          duration: 0,
          message: `Error processing files: Previous file processing in progress`,
          type: 'error',
          dismissible: true,
          position: 'top',
        });
        this.dropHighlighted = false;
      } else {
        this.dropHighlighted = false;
        const filePromises = [];
        let folderPromise = null;

        const items = [...e.dataTransfer.items];

        const entries = await Promise.all(
          items.map(async (item) => (item.kind === "file" ? item.getAsFileSystemHandle() : null)),
        );

        for (const entry of entries) {
          if (entry) {
            if (entry.kind === "file") {
              filePromises.push(entry.getFile());
            } else if (entry.kind === "directory") {
              folderPromise = this.processFolderAndReturnFiles(entry);
              break;
            }
          }
        }
        let allFiles = [];

        if (folderPromise) {
          allFiles = await Promise.all([folderPromise]);
          allFiles = allFiles[0];
        }

        if (filePromises.length > 0) {
          allFiles = await Promise.all(filePromises);
          // await this.compileFiles(fileObjs);
        }
        this.fileObjs = [];
        allFiles.forEach((file) => {
          const parts = file.name.split('.');
          let ext = "";
          if (parts.length > 1) {
            ext = parts.pop();
          }
          if (extensions && extensions.length > 0 && extensions.includes(ext)) {
            this.fileObjs.push(file);
          } else if (!extensions || (extensions && extensions.length === 0)) {
            this.fileObjs.push(file);
          }
        });
      }
    },
    chooseFiles(el) {
      document.getElementById(el).click();
    },
    async handleSelectFileInput(event) {
      this.fileObjs = Array.from(event.target.files);
    },
    removeFile(index) {
      this.fileObjs.splice(index, 1);
    },
  },
};
</script>

<style lang="scss" scoped>

.row + .row {
  margin-top: 1rem;
}

.source-row {
  position: relative;
  display: flex;
  flex-direction: row;
  width: 100%;

  &__folder-input {
    width: unset;
    flex: 1 1 auto;
    border-radius: 6px 0 0 6px;
    border-right: 1px solid transparent !important;

    &:focus {
      margin-right: 1px;
    }
  }

  &__folder-btn {
    flex: 0 1 auto;
    padding: 2px 8px;
    height: 100%;
    border-radius: 0 6px 6px 0;
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--body-text-color-secondary);
  }

  &__group-checkbox {
    display: flex;
    flex-direction: row;
    width: 100%;
  }
}

.error {
  @include themify() {
    background: rgba(182, 94, 94, 0.5) ;
    color: themed('color-error')
  }
}

.action-buttons {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.file-upload-container{
  display: flex;
  width: 100%;
}

.file-upload-btn {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 8px;
  align-items: center;
  justify-content: center;
  min-height: 150px;
  max-height: 250px;
  border: 2px dashed grey;
  border-radius: 8px;

  &__upload-icon {
    width: 60px;
    height: 60px;
    @include themify() {
      color: themed('color-primary');
    }
  }

  &__parsing-files{
    position: relative;
    width: 40px;
    height: 60px;
    left: -20px;
  }

  &__text {
    display: flex;
    flex-direction: column;

    & span {
      padding-top: 0.25rem;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      color: grey;

      font-size: 14px;
      font-weight: bold;

      .highlight {
        color: #250E81;
        font-size: 14px;
        cursor: pointer;
        &:hover {
          text-decoration: underline;
        }
      }
    }

  }

  &._highlight {
    padding: 10px;
    border: 3px dashed #250E81;
    background: #250e8125;
    animation-name: fill-upload-btn;
    animation-duration: 0.5s;

    & .file-upload-btn__upload-icon {
      width: 56px;
      height: 56px;
      animation-name: grow-upload-icon;
      animation-duration: 0.05s;
    }
    @keyframes grow-upload-icon {
      from {  width: 48px; height: 48px;}
      to {  width: 56px; height: 56px;}
    }

    @keyframes fill-upload-btn {
      from {  background: transparent;}
      to {  background: #250e8125;}
    }
  }
}

#file-upload-btn-subtext {
  display: flex;
  width: 100%;
  justify-content: start;
  color: var(--body-text-color-secondary);
  font-size: 13px;
  font-weight: 500;
}

.file-grid {
  display: grid;
  grid-template-columns: calc(50% - 5px) calc(50% - 5px);
  width: 100%;
  flex: 1 1 auto;
  gap: 10px;
  padding: 5px;
  overflow-y: auto;

  &__cell {
    display: flex;
    flex-direction: row;
    font-size: 14px;
    padding: 5px;
    height: 30px;
    border-radius: 8px;
    background-color: var(--color-white-800);
    align-items: center;
    justify-content: space-between;
    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &__remove-icon{
    @include themify() {
      color: themed('icon-color-primary');
    }
  }
}

.checkbox-label {
  margin-left: 10px;
  font-size: 14px;
  font-weight: 600;
}

.topics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, 220px);
  width: 100%;
  gap: 5px;
  padding: 5px;
  max-height: 500px;
  overflow-y: auto;
}

.help-icon {
  margin: 0 5px;
  color: var(--color-primary);
}

.help-icon:focus {
  outline: none;
}
</style>
